import React, { useRef } from "react";

import useRevealOnScroll from "./useRevealOnScroll";

const join = (...names) => names.filter(Boolean).join(" ");

// One element, its own observer.
//
// An earlier shape put the observer on the row and staggered the items inside
// it, so a single attribute drove four cards. That is wrong the moment the row
// becomes a column: the row's box spans all four stacked cards, so at
// `threshold: 0` the whole group flipped to shown as soon as the first card's
// top edge appeared, and cards two through four played their entrance entirely
// off screen and were already finished by the time they were scrolled to.
//
// Observing each item instead costs nothing and is correct at both widths. Four
// cards side by side cross the viewport edge at the same scroll offset, so their
// callbacks land in the same batch and the CSS transition-delay still staggers
// them; stacked, each is observed and revealed on its own. The delay itself is
// gated to md and up in _motion.scss for the same reason.
//
// `index` is a position, not a duration. The step lives in the stylesheet, so
// the whole page can be retuned from one declaration rather than by editing
// every call site — which is the entire point of having a lab to tune it in.
//
// Renders the element itself rather than wrapping one, and is meant to carry the
// grid column's classes directly. The service cards already own their transform
// — `.info.service-card:hover` lifts them 5px, `.card` lifts the team cards 10px
// on a bouncy cubic-bezier(0.34, 2, 0.6, 1) — and a reveal setting transform on
// those same elements would inherit that easing and fight the hover. Nesting
// inside a reactstrap <Col> is not an option either: it has no forwardRef, and
// an extra div between the column and `.service-card-link` breaks the
// `height: 100%` chain that gives the four cards equal heights above 768px.
function Reveal({
  as: Tag = "div",
  index = 0,
  className,
  style,
  children,
  ...rest
}) {
  const ref = useRef(null);
  useRevealOnScroll(ref);

  return (
    <Tag
      ref={ref}
      className={join("reveal", className)}
      // Omitted entirely at index 0 rather than written as "0", so the common
      // case adds no inline style at all and the stylesheet's own fallback
      // supplies the zero.
      style={index ? { ...style, "--reveal-index": index } : style}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
